<template>
  <transition mode="out-in" name="fade">
    <div v-if="slider" :class="classes.transitionWrapper">
      <component :is="slider">
        <CardSelector
          v-for="(market, i) in cards"
          :key="String(i)"
          :cardNumber="String(i + 1)"
          :cardType="market[0]"
          :marketIds="market[1]"
        />
      </component>
    </div>
    <div
      v-else-if="cards.length"
      :class="classes.transitionWrapper"
      :style="{ overflow: 'hidden' }"
    >
      <CardSelector :cardType="cards[0][0]" :marketIds="cards[0][1]" />
    </div>
  </transition>
</template>

<script lang="ts">
import CardSelector from "./CardSelector.vue";
import {
  computed,
  ComputedRef,
  defineAsyncComponent,
  defineComponent,
  Ref,
  ref,
  watchEffect
} from "vue";
import { isTouchDevice } from "../lib/isTouchDevice";
import { provideCarouselContext } from "../lib/provideInjectors";
import { useShouldDisplayOffer } from "../hooks/useDisplayOffer";
import { injectBetData } from "@fresh8/ad-data-composition/src/betData";
import { createUseStyles } from "@fresh8/vue-jss/src";
import { injectDisplayEvents } from "../composition/displayEvents";
import { injectPreview } from "@fresh8/ad-data-composition/src/preview";
import {
  useTemplateSize,
  MOBILE_LEADERBOARD,
  LMLB
} from "@fresh8/ad-data-composition/src/useTemplateSize";
import { injectBrand } from "@fresh8/ad-data-composition/src/brand";
import {
  enabledParlaySizes,
  enabledH2HSizes
} from "@fresh8/types/src/featureFlags";
import { injectInsightTestingContext } from "@fresh8/ads/src/lib/provideInjectors";
import { AdTemplate } from "@fresh8/types";
import { provideCardsContext } from "../lib/card-analytics";
import {
  useHandleCardAnalytics,
  createCardsContext,
  injectCardsContext
} from "../lib/card-analytics";
import { AnalyticEvents, CardsContext } from "@fresh8/types/src/analytics";
import { BetData } from "@fresh8/types/src/betData";
import { provideAnimationSync } from "../hooks/useAnimationSync";

const useStyles = createUseStyles({
  transitionWrapper: {
    flex: "1",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box"
  }
});

export default defineComponent({
  name: "Content",
  components: {
    CardSelector
  },
  setup() {
    const classes = useStyles();
    const { parlayMarkets, generateCardsSequence } = injectDisplayEvents();
    const isTouch = isTouchDevice();
    const isCarousel = ref(false);
    const itemsOnView = ref(0);
    const shouldDisplayOffer = useShouldDisplayOffer();
    const betDataContext = injectBetData();
    const previewContext = injectPreview();
    const { size } = useTemplateSize();
    const brand = injectBrand();
    const insightContext = injectInsightTestingContext();
    const $handleCardAnalytics = useHandleCardAnalytics();
    const cardsC = injectCardsContext();

    provideAnimationSync();

    provideCarouselContext(
      ref({
        isCarousel,
        itemsOnView
      })
    );
    const useParlayCard = computed(
      () =>
        enabledParlaySizes.includes(size.value.name) &&
        ((brand?.adTemplate.value as AdTemplate).eventCardOrder || []).indexOf(
          "parlay"
        ) > -1 &&
        parlayMarkets.value.length === 3
    );

    const useH2hCard = computed(() => {
      const h2hCard =
        enabledH2HSizes.includes(size.value.name) &&
        (brand?.adTemplate.value?.eventCardOrder || []).indexOf("h2h") > -1;
      return h2hCard;
    });

    const cards = computed(() => {
      return generateCardsSequence({
        betEvents: betDataContext?.betData.value?.events,
        betMarkets: betDataContext?.betData.value?.markets,
        size: size.value,
        insightRandomTesting: insightContext.value.insightRandomTesting,
        insights: brand?.insights.value,
        features: {
          isPreview: !!previewContext?.preview.value.isPreview,
          shouldDisplayOffer: shouldDisplayOffer.value,
          useH2hCard: !!useH2hCard.value,
          useParlayCard: !!useParlayCard.value,
          previewParlayOnly: !!previewContext?.preview.value.previewParlayOnly,
          previewH2hOnly: !!previewContext?.preview.value.previewH2hOnly,
          previewInsightOnly: !!previewContext?.preview.value.previewInsightOnly
        }
      });
    });
    const cardContext = createCardsContext(
      betDataContext?.betData as Ref<BetData>,
      cards
    );
    provideCardsContext(cardContext as ComputedRef<CardsContext>);
    $handleCardAnalytics(
      AnalyticEvents.IMPRESSION,
      "Impression",
      cardContext.value?.cards || []
    );

    const slider = computed(() => {
      // Never display the slider on the mobile leaderboard and pro evo size
      if (
        size.value.name === MOBILE_LEADERBOARD.name ||
        size.value.name === LMLB.name
      )
        return false;

      if ((cards.value?.length || 0) > 1) {
        if (isTouch) {
          return defineAsyncComponent(() => import("./TouchSlider"));
        }
        return defineAsyncComponent(() => import("./Carousel"));
      }

      return false;
    });

    watchEffect(() => {
      if ((cards.value?.length || 0) > 1) {
        isCarousel.value = true;
      }
    });

    // If no markets and no offer then collapse the ad
    watchEffect(() => {
      if (
        (cards.value?.length || 0) === 0 &&
        !previewContext?.preview.value.isPreview
      ) {
        window.parent.postMessage({ type: "f8_failed_loading" }, "*");
        document.documentElement.style.height = "0px";
        document.documentElement.style.overflow = "hidden";
        document.body.style.height = "0px";
        document.body.style.overflow = "hidden";
      } else if (document.documentElement.style.overflow === "hidden") {
        document.documentElement.style.height = "";
        document.documentElement.style.overflow = "";
        document.body.style.height = "";
        document.body.style.overflow = "";
      }

      const newCardContext = createCardsContext(
        betDataContext?.betData as Ref<BetData>,
        cards
      );

      cardsC.value.cards = newCardContext?.value?.cards || [];
    });

    return {
      classes,
      slider,
      cards
    };
  }
});
</script>
